import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ClinicWithConditionsAndPromo } from '../../types';
import Yup from '../../../../utils/yup';

interface UseClinicFormProps {
  clinic?: ClinicWithConditionsAndPromo | null;
  selectedClinicToClone?: string | null;
  isCreateMode?: boolean;
}

const useClinicForm = ({ clinic, selectedClinicToClone, isCreateMode = false }: UseClinicFormProps = {}) => {
  return useForm<ClinicWithConditionsAndPromo>({
    defaultValues: clinic ?? emptyClinic,
    resolver: yupResolver(schema)
  });
};

const shopCardSchema = Yup.object().shape({
  title: Yup.string().required(),
  description: Yup.string().required(),
  tagline: Yup.string().optional(),
  image_url: Yup.string().required()
});

// All optional so existing clinics still validate; row inner fields are required only once a row exists.
const heroBenefitsSchema = Yup.array()
  .of(
    Yup.object().shape({
      icon_url: Yup.string().required(),
      text: Yup.string().required()
    })
  )
  .optional();

const howItWorksSchema = Yup.object().shape({
  title: Yup.string().optional(),
  subtitle: Yup.string().optional(),
  steps: Yup.array()
    .of(
      Yup.object().shape({
        title: Yup.string().required(),
        description: Yup.string().required(),
        time_label: Yup.string().optional(),
        icon_url: Yup.string().optional()
      })
    )
    .optional()
}).optional();

const faqsSchema = Yup.array()
  .of(
    Yup.object().shape({
      question: Yup.string().required(),
      answer: Yup.string().required()
    })
  )
  .optional();

const optionalHexColorSchema = Yup.string()
  .trim()
  .matches(/^#(?:[0-9a-fA-F]{3}){1,2}$/i, { excludeEmptyString: true, message: 'Color is not a HEX value' })
  .optional();

const ctaBannerSchema = Yup.object().shape({
  image_url: Yup.string().optional(),
  heading: Yup.string().optional(),
  subtitle: Yup.string().optional(),
  button: Yup.object().shape({
    label: Yup.string().optional()
  }).optional()
}).optional();

// Set as a whole by ClinicalReviewEditor; the API requires the date whenever a reviewer is named.
const clinicalReviewSchema = Yup.object().shape({
  prescriber_id: Yup.string().optional(),
  reviewed_at: Yup.string().when('prescriber_id', {
    is: (prescriberId: string | undefined) => Boolean(prescriberId),
    then: Yup.string().matches(/^\d{4}-\d{2}-\d{2}$/, 'Pick a date').required('Pick a date'),
    otherwise: Yup.string().optional()
  })
}).nullable().optional();

const schema = Yup.object().shape({
  name: Yup.string().required(),
  spotlight_condition_id: Yup.string().nullable().optional(),
  promotional_content: Yup.object().shape({
    title: Yup.string().required(),
    description: Yup.string().required(),
    disclaimer: Yup.string().required(),
    home_card: Yup.object().shape({
      title: Yup.string().required(),
      description: Yup.string().required(),
      image_url: Yup.string().required()
    }),
    shop_card: shopCardSchema,
    zinger: Yup.object().shape({
      description: Yup.string().required(),
      icon_url: Yup.string().required()
    }),
    hero_benefits: heroBenefitsSchema,
    how_it_works: howItWorksSchema,
    faqs: faqsSchema,
    clinical_review: clinicalReviewSchema
  }),
  clinic_conditions: Yup.array().of(
    Yup.object().shape({
      _id: Yup.string().required(),
      condition_id: Yup.string().required(),
      questionnaire_id: Yup.string().nullable(),
      returning_questionnaire_id: Yup.string().nullable(),
      returning_order_history_period_in_days: Yup.number().nullable(),
      gender: Yup.string().optional(),
      spotlight_id: Yup.string().nullable().optional(),
      prescribing_protocol_link: Yup.string()
        .matches(/^https:\/\/.+/, { excludeEmptyString: true, message: 'Must be an https:// URL' })
        .nullable()
        .optional(),
      promotional_content: Yup.object().shape({
        title: Yup.string().required(),
        description: Yup.string().required(),
        image_url: Yup.string().required(),
        disclaimer: Yup.string().required(),
        background_color: Yup.string().trim().matches(/^#(?:[0-9a-fA-F]{3}){1,2}$/i, 'Color is not a HEX value').required(),
        shop_card: shopCardSchema,
        hero_title: Yup.string().optional(),
        subtitle: Yup.string().optional(),
        hero_benefits: heroBenefitsSchema,
        faqs: faqsSchema,
        cta_banner: ctaBannerSchema,
        how_it_works: howItWorksSchema,
        clinical_review: clinicalReviewSchema,
        about_condition: faqsSchema,
        headline: Yup.string().optional(),
        gender_images: Yup.object().shape({
          female: Yup.string().optional(),
          male: Yup.string().optional()
        }).optional(),
        primary_color: optionalHexColorSchema,
        secondary_color: optionalHexColorSchema
      }),
      is_video_call_required: Yup.boolean().required()
    })
  )
});

const emptyClinic: ClinicWithConditionsAndPromo = {
  clinic_conditions: [],
  promotional_content: {
    title: '',
    description: '',
    disclaimer: '',
    home_card: {
      title: '',
      description: '',
      image_url: ''
    },
    shop_card: {
      title: '',
      description: '',
      image_url: ''
    },
    zinger: {
      description: '',
      icon_url: ''
    },
    hero_benefits: [],
    how_it_works: {
      title: '',
      subtitle: '',
      steps: []
    },
    faqs: []
  },
  name: '',
  internal_prescribing: false,
  internal_product_charge: false,
  esignature_enabled: false,
};

export default useClinicForm;
